// web-frontend/src/shared/services/meetingsService.js
// 🔥 apiClient를 사용하여 토큰 자동 주입 - authService에서 설정된 토큰이 자동으로 Authorization 헤더에 포함됨
// 🔥🔥🔥 2025-12-31 수정: 모든 템플릿 리터럴 문법 오류 완전 수정
// 🔥🔥🔥 수정 내용: 백틱(`)을 함수 호출 괄호() 안에 올바르게 배치
// 🔥🔥🔥 2025-12-29 수정: rescheduleMeeting에 location_details 파라미터 추가

import apiClient from './apiClient';
import i18nService from './i18n';
import logger from '../utils/logger';
import {
  formatDateTimeInAppTimeZone,
  formatInAppTimeZone,
  formatTimeInAppTimeZone,
} from '../utils/dateTime';

class MeetingsService {
  constructor() {
    this.baseURL = '/meetings';
    this.retryAttempts = 0;
    this.maxRetryAttempts = 2;
  }

  /**
   * API 요청 헬퍼 메서드
   * ✅ apiClient가 토큰을 자동으로 헤더에 포함시킵니다.
   */
  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      timeout: 10000,
      ...options,
    };

    try {
      const response = await apiClient.request({
        url,
        ...config,
      });

      // apiClient가 이미 response.data를 반환하는지 확인
      if (response && response.data !== undefined) {
        return response.data; // axios 기본 구조
      } else {
        return response; // 이미 data가 추출된 경우
      }
    } catch (error) {
      // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
      logger.error(`API 요청 실패 [${url}]:`, error);
      throw this._processError(error);
    }
  }

  // =================== 🆕 채팅 연동 기능 ===================

  /**
   * 🔥🔥🔥 채팅에서 미팅 제안
   * 2025-12-25 수정: 필수 파라미터 검증 및 응답 데이터 정규화 추가
   * 2025-12-29 수정: receiverBoothNumber 파라미터 추가
   */
  async proposeMeetingFromChat(proposalData) {
    try {
      logger.info('📤 채팅에서 미팅 제안:', proposalData);

      // 🔥🔥🔥 [추가] 필수 파라미터 검증
      if (!proposalData.chatId) {
        throw new Error('채팅방 ID가 필요합니다.');
      }
      if (!proposalData.receiverUserId) {
        throw new Error('수신자 ID가 필요합니다.');
      }
      if (!proposalData.proposedDatetime) {
        throw new Error('미팅 일시가 필요합니다.');
      }

      const response = await this.makeRequest('/propose-from-chat', {
        method: 'POST',
        data: {
          chat_id: proposalData.chatId,
          receiver_user_id: proposalData.receiverUserId,
          proposal: {
            meeting_type: proposalData.meetingType || 'business',
            proposed_datetime: proposalData.proposedDatetime,
            duration_minutes: proposalData.durationMinutes || 60,
            location_type: proposalData.locationType || 'booth',
            location_details: proposalData.locationDetails || '',
            booth_id: proposalData.boothId || null,
            agenda: proposalData.agenda || '',
            notes: proposalData.notes || '',
            // 🔥🔥🔥 [2025-12-29 추가] 수신자 부스 번호
            receiver_booth_number: proposalData.receiverBoothNumber || null,
          },
          message_content: proposalData.messageContent || '',
        },
      });

      logger.info('✅ 미팅 제안 성공:', response);

      // 🔥🔥🔥 [추가] 응답 데이터 정규화
      if (response && typeof response === 'object') {
        return {
          ...response,
          meeting_id: response.meeting_id || response.id,
          message_id: response.message_id || response.message?.id,
          chat_id: response.chat_id || proposalData.chatId,
        };
      }

      return response;
    } catch (error) {
      logger.error('❌ 미팅 제안 실패:', error);
      throw error;
    }
  }

  /**
   * 🔥🔥🔥 미팅 초대 응답 (수락/거절)
   * 2025-12-25 수정: meetingId 검증 추가 및 재시도 로직 추가
   * @param {string|UUID} meetingId - 미팅 ID
   * @param {string} action - 'accept' 또는 'reject'
   * @param {string|null} responseMessage - 응답 메시지 (선택사항)
   */
  async getAvailabilityMatrix({ chatId, counterpartId, durationMinutes = 60 }) {
    try {
      return await this.makeRequest('/availability/matrix', {
        method: 'POST',
        data: {
          chat_id: chatId,
          counterpart_id: counterpartId,
          duration_minutes: durationMinutes,
        },
      });
    } catch (error) {
      logger.error('미팅 가능 시간표 조회 실패:', error);
      throw error;
    }
  }

  async createCandidateProposalFromChat(proposalData) {
    try {
      return await this.makeRequest('/proposals/from-chat', {
        method: 'POST',
        data: {
          chat_id: proposalData.chatId,
          receiver_user_id: proposalData.receiverUserId,
          selected_slot_keys: proposalData.selectedSlotKeys,
          candidates: proposalData.candidates || null,
          agenda: proposalData.agenda,
          notes: proposalData.notes || '',
          duration_minutes: proposalData.durationMinutes || 60,
          message_content: proposalData.messageContent || null,
          reschedule_original_meeting_id: proposalData.rescheduleOriginalMeetingId || null,
          reschedule_reason: proposalData.rescheduleReason || null,
          countered_original_message_id: proposalData.counteredOriginalMessageId || null,
        },
      });
    } catch (error) {
      logger.error('후보형 미팅 제안 전송 실패:', error);
      throw error;
    }
  }

  async acceptProposalSlot(messageId, slotIndex) {
    try {
      return await this.makeRequest(`/proposals/${messageId}/accept-slot`, {
        method: 'POST',
        data: { slot_index: slotIndex },
      });
    } catch (error) {
      logger.error('미팅 후보 수락 실패:', error);
      throw error;
    }
  }

  async declineProposal(messageId, reason = '') {
    try {
      return await this.makeRequest(`/proposals/${messageId}/decline`, {
        method: 'POST',
        data: { reason },
      });
    } catch (error) {
      logger.error('미팅 후보 거절 실패:', error);
      throw error;
    }
  }

  async respondToInvitation(meetingId, action, responseMessage = null) {
    try {
      // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
      logger.info(`📤 미팅 초대 응답: ${meetingId}, action=${action}`);

      // 🔥🔥🔥 [추가] meetingId 검증
      if (!meetingId) {
        throw new Error('미팅 ID가 필요합니다.');
      }

      // 백엔드 API는 'response' 필드를 사용하고, 'accept' | 'reject'만 허용
      if (!['accept', 'reject'].includes(action)) {
        // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
        throw new Error(`유효하지 않은 action: ${action}. 'accept' 또는 'reject'만 가능합니다.`);
      }

      const requestData = {
        response: action, // 백엔드 스펙: 'accept' | 'reject'
      };

      // 🔥 응답 메시지가 있을 때만 포함
      if (responseMessage) {
        requestData.response_message = responseMessage;
      }

      // 🔥🔥🔥 [추가] 재시도 로직 (네트워크 불안정 대응)
      let lastError;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
          const response = await this.makeRequest(`/${meetingId}/respond`, {
            method: 'POST',
            data: requestData,
          });

          logger.info('✅ 초대 응답 성공:', response);
          return response;
        } catch (error) {
          lastError = error;
          // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
          logger.warn(`⚠️ 초대 응답 시도 ${attempt}/3 실패:`, error.message);

          // 서버 에러(5xx) 또는 네트워크 에러인 경우에만 재시도
          const shouldRetry =
            error.message?.includes('Network') ||
            error.message?.includes('timeout') ||
            error.message?.includes('네트워크') ||
            (error.status && error.status >= 500);

          if (attempt < 3 && shouldRetry) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          } else if (!shouldRetry) {
            throw error;
          }
        }
      }

      throw lastError;
    } catch (error) {
      logger.error('❌ 초대 응답 실패:', error);
      throw error;
    }
  }

  /**
   * 🔥 미팅 응답 (간소화 버전) - 참고코드 스타일
   * @param {string|UUID} meetingId - 미팅 ID
   * @param {string} status - 'accepted' | 'rejected'
   * @param {string} message - 응답 메시지 (선택사항)
   */
  async respondToMeeting(meetingId, status, message = '') {
    try {
      logger.info('📬 미팅 응답:', { meetingId, status });

      // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
      const response = await apiClient.post(`/meetings/${meetingId}/respond`, {
        status, // 'accepted' | 'rejected'
        message,
      });

      logger.info('✅ 미팅 응답 성공');
      return response.data || response;
    } catch (error) {
      logger.error('❌ 미팅 응답 실패:', error);
      throw error;
    }
  }

  /**
   * 🔥 미팅 수락
   */
  async acceptMeeting(meetingId, message = null) {
    try {
      const result = await this.respondToInvitation(meetingId, 'accept', message);
      logger.info('✅ 미팅 수락 성공');
      return result;
    } catch (error) {
      logger.error('❌ 미팅 수락 실패:', error);
      throw error;
    }
  }

  /**
   * 🔥 미팅 거절
   */
  async rejectMeeting(meetingId, reason = null) {
    try {
      const result = await this.respondToInvitation(meetingId, 'reject', reason);
      logger.info('✅ 미팅 거절 성공');
      return result;
    } catch (error) {
      logger.error('❌ 미팅 거절 실패:', error);
      throw error;
    }
  }

  /**
   * @deprecated 'declineMeeting'은 'rejectMeeting'으로 대체되었습니다.
   */
  async declineMeeting(meetingId, reason = null) {
    logger.warn('⚠️ declineMeeting은 deprecated되었습니다. rejectMeeting을 사용하세요.');
    return this.rejectMeeting(meetingId, reason);
  }

  /**
   * 알림 스케줄 포함 미팅 조회
   */
  async getMeetingWithNotifications(meetingId) {
    try {
      logger.info('📋 알림 포함 미팅 조회:', meetingId);

      // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
      const response = await this.makeRequest(`/${meetingId}/with-notifications`, {
        method: 'GET',
      });

      logger.info('✅ 알림 포함 미팅 조회 성공');
      return response;
    } catch (error) {
      logger.error('❌ 알림 포함 미팅 조회 실패:', error);
      throw error;
    }
  }

  // =================== 기본 미팅 기능 ===================

  /**
   * 🔥 다가오는 미팅 조회 - 참고코드 스타일 적용
   * @param {number} days - 조회할 기간 (일), 기본값 7
   * @param {number} limit - 최대 개수, 기본값 50
   */
  async getUpcomingMeetings(params = {}) {
    try {
      // params가 객체인 경우와 개별 인자인 경우 모두 처리
      let days, limit;

      if (typeof params === 'object') {
        days = Number(params.days) || 7;
        limit = Number(params.limit) || 50;
      } else {
        // 이전 버전 호환: getUpcomingMeetings(days, limit) 형태
        days = Number(params) || 7;
        limit = Number(arguments[1]) || 50;
      }

      logger.info('📋 다가오는 미팅 조회:', { days, limit });

      // ✅ apiClient가 토큰을 자동으로 헤더에 포함
      const response = await apiClient.get('/meetings/upcoming', {
        params: { days, limit },
      });

      // 🔥 백엔드 응답 구조 처리
      let meetings = [];
      const data = response.data || response;

      if (!data) {
        logger.warn('⚠️ 응답 데이터 없음');
        return [];
      }

      if (Array.isArray(data)) {
        meetings = data;
      } else if (typeof data === 'object') {
        if (Array.isArray(data.data)) {
          meetings = data.data;
        } else if (Array.isArray(data.meetings)) {
          meetings = data.meetings;
        } else if (Array.isArray(data.data?.meetings)) {
          meetings = data.data.meetings;
        }
      }

      // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
      logger.info(`✅ 다가오는 미팅 조회 성공: ${meetings.length}개`);

      return meetings;
    } catch (error) {
      logger.error('❌ 다가오는 미팅 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 🔥 미팅 목록 조회 (페이징 지원)
   */
  async getMeetings(params = {}) {
    try {
      const {
        page = 1,
        limit = 20,
        status = '',
        meeting_type = '',
        sort_by = 'created_at',
        sort_order = 'desc',
        days = null,
        ...filters
      } = params;

      logger.info('📋 미팅 목록 조회:', { page, limit, status, meeting_type });

      const queryParams = {
        page,
        limit,
        sort_by,
        sort_order,
        ...filters,
      };

      if (status) queryParams.status = status;
      if (meeting_type) queryParams.meeting_type = meeting_type;
      if (days) queryParams.days = days;

      // ✅ apiClient가 토큰을 자동으로 헤더에 포함
      const response = await this.makeRequest('/', {
        method: 'GET',
        params: queryParams,
      });

      // 🔥 백엔드 응답 구조 처리
      let meetings = [];
      let total = 0;
      let currentPage = page;

      if (!response) {
        return {
          meetings: [],
          total: 0,
          page: currentPage,
          limit,
        };
      }

      if (Array.isArray(response)) {
        meetings = response;
        total = meetings.length;
      } else if (typeof response === 'object') {
        if (Array.isArray(response.meetings)) {
          meetings = response.meetings;
          total = response.total || meetings.length;
          currentPage = response.page || page;
        } else if (Array.isArray(response.data)) {
          meetings = response.data;
          total = response.total || meetings.length;
        } else if (Array.isArray(response.data?.meetings)) {
          meetings = response.data.meetings;
          total = response.data.total || response.total || meetings.length;
          currentPage = response.data.page || response.page || page;
        }
      }

      // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
      logger.info(`✅ 미팅 목록 조회 성공: ${meetings.length}개 (total: ${total})`);

      return {
        meetings,
        total,
        page: currentPage,
        limit,
      };
    } catch (error) {
      logger.error('❌ 미팅 목록 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 🔥 미팅 상세 정보 조회 - 참고코드 스타일 적용
   */
  async getMeetingDetail(meetingId) {
    try {
      logger.info('📋 미팅 상세 조회:', meetingId);

      // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
      const response = await apiClient.get(`/meetings/${meetingId}`);

      logger.info('✅ 미팅 상세 조회 성공');
      return response.data || response;
    } catch (error) {
      logger.error('❌ 미팅 상세 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 미팅 상세 조회 (별칭)
   */
  async getMeeting(meetingId) {
    return this.getMeetingDetail(meetingId);
  }

  /**
   * 🔥 미팅 생성 - 참고코드 스타일 적용
   */
  async createMeeting(meetingData) {
    try {
      logger.info('📋 미팅 생성:', meetingData);

      // 스키마 필드명 매핑 (백엔드 API 스펙에 맞춤)
      const requestData = {
        title: meetingData.title,
        description: meetingData.description,
        meeting_type: meetingData.meeting_type || meetingData.meetingType || 'business',
        location: meetingData.location || meetingData.location_details || meetingData.locationDetails || '위치 미정',
        location_type: meetingData.location_type || meetingData.locationType || 'booth',
        location_details: meetingData.location_details || meetingData.locationDetails,
        booth_id: meetingData.booth_id || meetingData.boothId,
        online_meeting_url: meetingData.online_meeting_url || meetingData.onlineMeetingUrl,
        scheduled_start: meetingData.scheduled_start || meetingData.scheduledStart,
        scheduled_end: meetingData.scheduled_end || meetingData.scheduledEnd,
        duration_minutes: meetingData.duration_minutes || meetingData.durationMinutes || 60,
        priority: meetingData.priority || 'medium',
        participant_ids: meetingData.participant_ids || meetingData.participantIds || [],
        agenda_items: meetingData.agenda_items || meetingData.agendaItems,
        special_requirements: meetingData.special_requirements || meetingData.specialRequirements,
        is_recurring: meetingData.is_recurring || meetingData.isRecurring || false,
        recurrence_pattern: meetingData.recurrence_pattern || meetingData.recurrencePattern,
        recurrence_end_date: meetingData.recurrence_end_date || meetingData.recurrenceEndDate,
        company_id: meetingData.company_id || meetingData.companyId,
        invited_user_id: meetingData.invited_user_id || meetingData.invitedUserId,
        max_participants: meetingData.max_participants || meetingData.maxParticipants,
        agenda: meetingData.agenda,
        status: meetingData.status || 'requested',
        metadata: meetingData.metadata || {},
      };

      // ✅ apiClient가 토큰을 자동으로 헤더에 포함
      const response = await apiClient.post('/meetings', requestData);

      logger.info('✅ 미팅 생성 성공');
      return response.data || response;
    } catch (error) {
      logger.error('❌ 미팅 생성 실패:', error);
      throw error;
    }
  }

  /**
   * 미팅 요청 (간소화된 생성)
   */
  async requestMeeting(requestData) {
    try {
      logger.info('📋 미팅 요청:', requestData);

      const response = await this.makeRequest('/request', {
        method: 'POST',
        data: requestData,
      });

      logger.info('✅ 미팅 요청 성공');
      return response;
    } catch (error) {
      logger.error('❌ 미팅 요청 실패:', error);
      throw error;
    }
  }

  /**
   * 미팅 정보 업데이트
   */
  async updateMeeting(meetingId, updateData) {
    try {
      logger.info('📋 미팅 업데이트:', meetingId, updateData);

      // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
      const response = await this.makeRequest(`/${meetingId}`, {
        method: 'PATCH',
        data: updateData,
      });

      logger.info('✅ 미팅 업데이트 성공');
      return response;
    } catch (error) {
      logger.error('❌ 미팅 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 미팅 상태 업데이트
   */
  async updateMeetingStatus(meetingId, status) {
    try {
      return await this.updateMeeting(meetingId, { status });
    } catch (error) {
      logger.error('❌ 미팅 상태 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 🔥🔥🔥 미팅 취소 - 백엔드 MeetingCancellation 스키마에 맞게 수정
   *
   * 백엔드 스키마 (MeetingCancellation):
   * - reason: str (필수, 최소 5자, 최대 500자)
   * - notify_participants: bool (기본 true)
   * - offer_reschedule: bool (기본 false)
   * - cancel_recurring: bool (기본 false)
   */
  async cancelMeeting(meetingId, reason) {
    try {
      logger.info('📋 미팅 취소:', meetingId, { reason });

      // 🔥🔥🔥 [핵심 수정] reason이 없거나 5자 미만이면 기본값 사용
      // 백엔드 MeetingCancellation 스키마: reason은 필수이고 최소 5자
      let finalReason = reason?.trim();
      if (!finalReason || finalReason.length < 5) {
        finalReason = '미팅 취소 요청'; // 기본값 (5자 이상)
      }

      const requestData = {
        reason: finalReason,
        notify_participants: true,
        offer_reschedule: false,
        cancel_recurring: false,
      };

      logger.debug('📤 취소 요청 데이터:', requestData);

      // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
      const response = await this.makeRequest(`/${meetingId}/cancel`, {
        method: 'POST',
        data: requestData,
      });

      logger.info('✅ 미팅 취소 성공');
      return response;
    } catch (error) {
      logger.error('❌ 미팅 취소 실패:', error);
      throw error;
    }
  }

  /**
   * 🔥🔥🔥 [2025-12-29 수정] 미팅 일정 변경 - location_details 파라미터 추가
   * @param {string} meetingId - 미팅 ID
   * @param {string} newDatetime - 새로운 일시 (ISO 형식)
   * @param {string} reason - 변경 사유 (선택)
   * @param {string} locationDetails - 장소 정보 (선택) 🔥 NEW
   */
  async rescheduleMeeting(meetingId, newDatetime, reason, locationDetails = null) {
    try {
      logger.info('📋 미팅 일정 변경:', meetingId, { newDatetime, reason, locationDetails });

      const requestData = {
        new_datetime: newDatetime,
      };

      // reason이 있을 때만 포함
      if (reason && reason.trim()) {
        requestData.reason = reason.trim();
      }

      // 🔥🔥🔥 [2025-12-29 추가] location_details가 있을 때만 포함
      if (locationDetails && locationDetails.trim()) {
        requestData.location_details = locationDetails.trim();
      }

      logger.debug('📤 일정 변경 요청 데이터:', requestData);

      // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
      const response = await this.makeRequest(`/${meetingId}/reschedule`, {
        method: 'POST',
        data: requestData,
      });

      logger.info('✅ 미팅 일정 변경 성공');
      return response;
    } catch (error) {
      logger.error('❌ 미팅 일정 변경 실패:', error);
      throw error;
    }
  }

  /**
   * 미팅 일정 변경 제안
   */
  async proposeReschedule(meetingId, newScheduleData) {
    try {
      logger.info('🔄 일정 변경 제안:', { meetingId, newScheduleData });

      const response = await apiClient.post(
        `/meetings/${meetingId}/reschedule`,
        newScheduleData
      );

      logger.info('✅ 일정 변경 제안 성공');
      return response.data || response;
    } catch (error) {
      logger.error('❌ 일정 변경 제안 실패:', error);
      throw error;
    }
  }

  /**
   * 미팅 삭제
   */
  async deleteMeeting(meetingId) {
    try {
      logger.info('📋 미팅 삭제:', meetingId);

      // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
      const response = await this.makeRequest(`/${meetingId}`, {
        method: 'DELETE',
      });

      logger.info('✅ 미팅 삭제 성공');
      return response;
    } catch (error) {
      logger.error('❌ 미팅 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * 미팅 완료 처리
   */
  async completeMeeting(meetingId, notes = '') {
    try {
      logger.info('✅ 미팅 완료 처리:', { meetingId, notes });

      const response = await apiClient.post(
        `/meetings/${meetingId}/complete`,
        { notes }
      );

      logger.info('✅ 미팅 완료 처리 성공');
      return response.data || response;
    } catch (error) {
      logger.error('❌ 미팅 완료 처리 실패:', error);
      throw error;
    }
  }

  /**
   * 미팅 피드백 제출
   */
  async submitFeedback(meetingId, rating, feedback = null) {
    try {
      logger.info('📋 피드백 제출:', meetingId, rating);

      const requestData = { rating };
      if (feedback) {
        requestData.feedback = feedback;
      }

      // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
      const response = await this.makeRequest(`/${meetingId}/feedback`, {
        method: 'POST',
        data: requestData,
      });

      logger.info('✅ 피드백 제출 성공');
      return response;
    } catch (error) {
      logger.error('❌ 피드백 제출 실패:', error);
      throw error;
    }
  }

  /**
   * 미팅 통계 조회
   */
  async getMeetingStats(period = 'weekly') {
    try {
      logger.info('📋 미팅 통계 조회');

      const response = await this.makeRequest('/stats', {
        method: 'GET',
        params: { period },
      });

      logger.info('✅ 미팅 통계 조회 성공');
      return response;
    } catch (error) {
      logger.error('❌ 미팅 통계 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 내 미팅 통계 조회
   */
  async getMyMeetingStats() {
    try {
      const response = await apiClient.get('/meetings/stats');
      return response.data || response;
    } catch (error) {
      logger.error('❌ 미팅 통계 조회 실패:', error);
      throw error;
    }
  }

  // =================== 참가자 관리 ===================

  /**
   * 참가자 추가
   */
  async addParticipants(meetingId, userIds) {
    try {
      logger.info('📋 참가자 추가:', meetingId, userIds);

      // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
      const response = await this.makeRequest(`/${meetingId}/participants`, {
        method: 'POST',
        data: { user_ids: userIds },
      });

      logger.info('✅ 참가자 추가 성공');
      return response;
    } catch (error) {
      logger.error('❌ 참가자 추가 실패:', error);
      throw error;
    }
  }

  /**
   * 참가자 제거
   */
  async removeParticipant(meetingId, userId) {
    try {
      logger.info('📋 참가자 제거:', meetingId, userId);

      // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
      const response = await this.makeRequest(`/${meetingId}/participants/${userId}`, {
        method: 'DELETE',
      });

      logger.info('✅ 참가자 제거 성공');
      return response;
    } catch (error) {
      logger.error('❌ 참가자 제거 실패:', error);
      throw error;
    }
  }

  /**
   * 미팅 참석자 추가
   */
  async addAttendee(meetingId, userId) {
    try {
      logger.info('👥 참석자 추가:', { meetingId, userId });

      const response = await apiClient.post(
        `/meetings/${meetingId}/attendees`,
        { user_id: userId }
      );

      logger.info('✅ 참석자 추가 성공');
      return response.data || response;
    } catch (error) {
      logger.error('❌ 참석자 추가 실패:', error);
      throw error;
    }
  }

  /**
   * 미팅 참석자 제거
   */
  async removeAttendee(meetingId, userId) {
    try {
      logger.info('👥 참석자 제거:', { meetingId, userId });

      const response = await apiClient.delete(
        `/meetings/${meetingId}/attendees/${userId}`
      );

      logger.info('✅ 참석자 제거 성공');
      return response.data || response;
    } catch (error) {
      logger.error('❌ 참석자 제거 실패:', error);
      throw error;
    }
  }

  // =================== 가용성 및 충돌 체크 ===================

  /**
   * 공통 가용 시간 찾기
   */
  async findCommonAvailability(participantIds, dateFrom, dateTo, durationMinutes) {
    try {
      logger.info('📋 공통 가용 시간 조회');

      const response = await this.makeRequest('/availability/common', {
        method: 'POST',
        data: {
          participant_ids: participantIds,
          date_from: dateFrom,
          date_to: dateTo,
          duration_minutes: durationMinutes,
        },
      });

      logger.info('✅ 공통 가용 시간 조회 성공');
      return response;
    } catch (error) {
      logger.error('❌ 공통 가용 시간 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 일정 충돌 확인
   */
  async checkConflicts(participantIds, proposedTime, durationMinutes, excludeMeetingId = null) {
    try {
      logger.info('📋 일정 충돌 확인');

      const response = await this.makeRequest('/conflicts/check', {
        method: 'POST',
        data: {
          participant_ids: participantIds,
          proposed_time: proposedTime,
          duration_minutes: durationMinutes,
          exclude_meeting_id: excludeMeetingId,
        },
      });

      logger.info('✅ 일정 충돌 확인 성공');
      return response;
    } catch (error) {
      logger.error('❌ 일정 충돌 확인 실패:', error);
      throw error;
    }
  }

  // =================== 미팅룸 관련 ===================

  /**
   * 미팅룸 목록 조회
   */
  async getMeetingRooms(filters = {}) {
    try {
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const queryString = params.toString();
      // 🔥🔥🔥 [수정됨] 올바른 템플릿 리터럴 문법
      const url = `/meeting-rooms${queryString ? `?${queryString}` : ''}`;

      const response = await apiClient.request({
        url,
        method: 'GET',
      });

      return response.data || [];
    } catch (error) {
      logger.error('❌ 미팅룸 목록 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 미팅룸 예약
   */
  async bookMeetingRoom(roomId, meetingId, startTime, endTime) {
    try {
      const response = await apiClient.request({
        url: '/meeting-rooms/book',
        method: 'POST',
        data: {
          room_id: roomId,
          meeting_id: meetingId,
          start_time: startTime,
          end_time: endTime,
        },
      });

      logger.info('✅ 미팅룸 예약 성공');
      return response.data || response;
    } catch (error) {
      logger.error('❌ 미팅룸 예약 실패:', error);
      throw error;
    }
  }

  // =================== 유틸리티 메서드 ===================

  /**
   * 미팅 시간 포맷팅
   */
  formatMeetingDateTime(dateTime) {
    if (!dateTime) return '';

    const date = new Date(dateTime);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateKey = formatInAppTimeZone(date, 'en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' });
    const todayKey = formatInAppTimeZone(now, 'en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' });
    const tomorrowKey = formatInAppTimeZone(tomorrow, 'en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' });

    if (dateKey === todayKey) {
      return `오늘 ${formatTimeInAppTimeZone(date, 'ko-KR')}`;
    } else if (dateKey === tomorrowKey) {
      return `내일 ${formatTimeInAppTimeZone(date, 'ko-KR')}`;
    } else {
      return formatDateTimeInAppTimeZone(date, 'ko-KR', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  }

  /**
   * 미팅 지속시간 계산
   */
  calculateDuration(startTime, endTime) {
    if (!startTime || !endTime) return 60;

    const start = new Date(startTime);
    const end = new Date(endTime);
    return Math.max(0, Math.floor((end - start) / (1000 * 60)));
  }

  /**
   * 미팅 상태 한글 변환
   */
  getStatusText(status) {
    const statusMap = {
      requested: '요청됨',
      proposed: '제안됨',
      pending: '대기중',
      confirmed: '확정',
      scheduled: '예정',
      in_progress: '진행중',
      rescheduled: '일정변경',
      completed: '완료',
      cancelled: '취소',
      no_show: '불참',
    };

    return statusMap[status?.toLowerCase()] || status || '알 수 없음';
  }

  /**
   * 미팅 타입 한글 변환
   */
  getTypeText(type) {
    const typeMap = {
      one_on_one: '1:1 미팅',
      group: '그룹 미팅',
      presentation: '프레젠테이션',
      demo: '데모',
      consultation: '상담',
      negotiation: '협상',
      business: '비즈니스',
      networking: '네트워킹',
      investment: '투자',
      technical: '기술',
      partnership: '파트너십',
    };

    return typeMap[type?.toLowerCase()] || type || '일반 미팅';
  }

  /**
   * 초대 상태 한글 변환
   */
  getInvitationStatusText(status) {
    const statusMap = {
      proposed: '제안됨',
      pending: '대기중',
      accepted: '수락됨',
      rejected: '거절됨',
      declined: '거절됨', // 하위 호환성
      tentative: '미정',
      expired: '만료됨',
      cancelled: '취소됨',
    };

    return statusMap[status?.toLowerCase()] || status || '알 수 없음';
  }

  /**
   * 에러 처리
   * @private
   */
  _processError(error) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.detail || error.response.data?.message || error.message;

      switch (status) {
        case 400:
          return new Error(i18nService.t('error.badRequest', { message }));
        case 401:
          return new Error(i18nService.t('error.unauthorized'));
        case 403:
          return new Error(i18nService.t('error.forbidden'));
        case 404:
          return new Error(i18nService.t('error.notFoundResource'));
        case 422:
          return new Error(i18nService.t('error.unprocessable', { message }));
        case 429:
          return new Error(i18nService.t('error.tooManyRequests'));
        case 500:
          return new Error(i18nService.t('error.serverErrorRetry'));
        default:
          return new Error(i18nService.t('error.unknownStatus', { status, message }));
      }
    } else if (error.request) {
      return new Error(i18nService.t('error.networkError'));
    } else {
      return new Error(error.message || i18nService.t('error.unknownError'));
    }
  }

  /**
   * 서비스 상태 확인
   */
  getServiceStatus() {
    return {
      baseURL: this.baseURL,
      retryAttempts: this.retryAttempts,
      maxRetryAttempts: this.maxRetryAttempts,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * 서비스 초기화
   */
  reset() {
    this.retryAttempts = 0;
    logger.info('🔄 MeetingsService 초기화됨');
  }
}

// 싱글톤 인스턴스 생성 및 export
const meetingsService = new MeetingsService();

export default meetingsService;
export { meetingsService };
